/**
 * Обёртка партнёрских ссылок в наш серверный редиректор `/api/r`.
 * Нужна, чтобы обойти ad/DNS-блокировщики, режущие tp.media и подобные tracker-домены.
 * Все URL, созданные affiliate-builder'ом, проходят через first-party redirect.
 * Route повторно проверяет домен по фиксированному безопасному списку и списку
 * активных провайдеров из Sanity.
 */

import { isAllowedDestination, isTrackerHost } from './destinations';

const TRACKER_HOSTS = new Set(['tp.media', 'travelpayouts.com']);
const TRACKER_SUFFIXES = ['.tp.st', '.tpo.li', '.sjv.io'];

export function isTrackerUrl(url) {
    if (typeof url !== 'string' || !url) return false;
    try {
        const host = new URL(url).hostname.toLowerCase();
        if (TRACKER_HOSTS.has(host)) return true;
        return TRACKER_SUFFIXES.some(s => host === s.slice(1) || host.endsWith(s));
    } catch {
        return false;
    }
}

const PROXY_BASE = '/api/r';

export function wrapAffiliateLink(url, { provider, subId } = {}) {
    if (typeof url !== 'string' || !url || url === '#') return url || '#';
    if (url.startsWith(`${PROXY_BASE}?`)) return url;

    try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'https:') return url;
    } catch {
        return url;
    }

    const params = new URLSearchParams({ u: url });
    if (provider) params.set('provider', String(provider).slice(0, 80));
    if (subId) params.set('subid', String(subId).slice(0, 160));
    return `${PROXY_BASE}?${params.toString()}`;
}

/**
 * Оборачивает ссылку в `/api/r`, только если route её гарантированно пропустит
 * (трекер или домен из статического whitelist). Остальное возвращает как есть:
 * прямую ссылку вне whitelist route отдаёт 400, и обёртка сломала бы рабочий переход.
 * Аудит 2026-09-16: 477 партнёрских ссылок на /tours и /airport шли мимо /api/r.
 */
export function proxiedAffiliateHref(url, { provider, subId } = {}) {
    if (typeof url !== 'string' || !url) return url;
    if (url.startsWith(`${PROXY_BASE}?`)) {
        // Билдеры (Booking в ledger и др.) заворачивают без subId — метку страницы
        // добавляем здесь, route перенесёт её в sub_id/subId1 сети.
        if (!subId || new URLSearchParams(url.slice(PROXY_BASE.length + 1)).has('subid')) return url;
        return `${url}&subid=${encodeURIComponent(String(subId).slice(0, 160))}`;
    }
    let host;
    try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'https:') return url;
        host = parsed.hostname;
    } catch {
        return url;
    }
    if (!isTrackerHost(host) && !isAllowedDestination(host)) return url;
    return wrapAffiliateLink(url, { provider, subId });
}
